/*
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

// trebox-session — the SINGLE source of truth for the trebox web session +
// API transport. The console app, the login adapter, and the account adapter
// all import from here, so the token-key contract, JWT-claim decode, bearer
// injection, ADR-146 error shape, and the 401→refresh policy live in exactly
// one place (architect review M1 + M2). The legacy coze axios client
// (@trebox-arch/bot-http) speaks the coze `{code,msg}` envelope and cannot
// consume trebox-native ADR-146 `{error:{reason,message}}` responses — hence
// trebox surfaces transport through this module, not that client.

const ACCESS_TOKEN_KEY = 'trebox_access_token';
const REFRESH_TOKEN_KEY = 'trebox_refresh_token';
const TENANT_ID_KEY = 'trebox_tenant_id';
const PRINCIPAL_ID_KEY = 'trebox_principal_id';

const HTTP_UNAUTHORIZED = 401;
const ERR_BODY_MAX = 240;
const B64_GROUP = 4;
const JWT_MIN_PARTS = 2;

// Configurable backend API base (TREBOX_API_BASE_URL · build-time). Empty →
// same-origin. Prefixes absolute /api paths so fetch() targets the dedicated
// API host (e.g. https://api.emora.aikj.co) instead of the page origin.
const API_BASE_URL = process.env.TREBOX_API_BASE_URL || '';
const apiBase = (path: string): string =>
  API_BASE_URL && path.startsWith('/') ? API_BASE_URL + path : path;

const safeGet = (key: string): string => {
  try {
    return window.localStorage.getItem(key) || '';
  } catch {
    // localStorage can throw in sandboxed iframes — treat as anonymous.
    return '';
  }
};

export const getAccessToken = (): string => safeGet(ACCESS_TOKEN_KEY);
export const getRefreshToken = (): string => safeGet(REFRESH_TOKEN_KEY);
export const getActiveTenantId = (): string => safeGet(TENANT_ID_KEY);
export const getPrincipalId = (): string => safeGet(PRINCIPAL_ID_KEY);

// authHeader returns the bearer header, or {} when anonymous. Callers that go
// through apiFetch/apiJson do NOT need this — it is applied centrally.
export const authHeader = (): Record<string, string> => {
  const token = getAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export interface JwtClaims {
  tid?: string;
  sub?: string;
}

// decodeJwtClaims reads `tid` / `sub` off the JWT payload. No signature
// verification — the server is the trust boundary; this only keeps the
// display-side session keys in sync (the /select-tenant, /switch-tenant and
// /refresh token DTOs omit tenant_id / principal_id). base64url + missing
// padding are handled explicitly so atob doesn't throw.
export function decodeJwtClaims(token: string): JwtClaims {
  try {
    const parts = token.split('.');
    if (parts.length < JWT_MIN_PARTS) {
      return {};
    }
    let b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const pad = b64.length % B64_GROUP;
    if (pad) {
      b64 += '='.repeat(B64_GROUP - pad);
    }
    return JSON.parse(atob(b64)) as JwtClaims;
  } catch {
    return {};
  }
}

// persistSession writes the four session keys. tenant_id / principal_id are
// recovered from the JWT when not supplied (refresh / switch-tenant responses
// omit them). A missing refresh token is REMOVED rather than left stale, so a
// later refresh can never revert the active tenant/session.
export function persistSession(
  accessToken: string,
  refreshToken?: string,
  tenantId?: string,
): void {
  if (!accessToken) {
    return;
  }
  const claims = decodeJwtClaims(accessToken);
  try {
    window.localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    if (refreshToken) {
      window.localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    } else {
      window.localStorage.removeItem(REFRESH_TOKEN_KEY);
    }
    const tid = tenantId || claims.tid || '';
    const pid = claims.sub || '';
    if (tid) {
      window.localStorage.setItem(TENANT_ID_KEY, tid);
    }
    if (pid) {
      window.localStorage.setItem(PRINCIPAL_ID_KEY, pid);
    }
  } catch (e) {
    void e; // sandboxed iframe — best effort, nothing to recover.
  }
}

export function clearSession(): void {
  try {
    [
      ACCESS_TOKEN_KEY,
      REFRESH_TOKEN_KEY,
      TENANT_ID_KEY,
      PRINCIPAL_ID_KEY,
    ].forEach(k => window.localStorage.removeItem(k));
  } catch (e) {
    void e; // sandboxed iframe — best effort.
  }
}

// --- ADR-146 error envelope ---

export class TreboxApiError extends Error {
  status: number;
  reason: string;
  constructor(status: number, message: string, reason = '') {
    super(message);
    this.name = 'TreboxApiError';
    this.status = status;
    this.reason = reason;
  }
}

export async function toApiError(res: Response): Promise<TreboxApiError> {
  const text = await res.text().catch(() => '');
  let body: { error?: { reason?: string; message?: string } } | null = null;
  try {
    body = JSON.parse(text);
  } catch (e) {
    body = null; // not a JSON envelope — fall back to raw text
  }
  if (body?.error) {
    return new TreboxApiError(
      res.status,
      body.error.message || body.error.reason || `HTTP ${res.status}`,
      body.error.reason || '',
    );
  }
  return new TreboxApiError(
    res.status,
    `HTTP ${res.status} · ${text.slice(0, ERR_BODY_MAX)}`,
  );
}

// --- central transport: bearer + 401→refresh-once→retry ---

let refreshInFlight: Promise<boolean> | null = null;

async function doRefresh(): Promise<boolean> {
  const rt = getRefreshToken();
  if (!rt) {
    return false;
  }
  try {
    const r = await fetch(apiBase('/api/auth/refresh'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: rt }),
    });
    if (!r.ok) {
      return false;
    }
    const body = (await r.json()) as {
      access_token?: string;
      refresh_token?: string;
    };
    if (!body.access_token) {
      return false;
    }
    // Refresh keeps the same tenant — tenant_id comes from the new JWT.
    persistSession(body.access_token, body.refresh_token);
    return true;
  } catch {
    return false;
  }
}

// Single-flight: concurrent 401s share one refresh round-trip.
function refreshOnce(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = doRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

// apiFetch is the trebox transport: it injects the bearer and, on a 401 with a
// refresh token present, transparently refreshes once and retries. The
// /api/auth/refresh call itself uses raw fetch (no recursion).
export async function apiFetch(
  input: string,
  init: RequestInit = {},
): Promise<Response> {
  const send = (): Promise<Response> =>
    fetch(apiBase(input), {
      credentials: 'include',
      ...init,
      headers: { ...(init.headers as Record<string, string>), ...authHeader() },
    });
  let res = await send();
  if (res.status === HTTP_UNAUTHORIZED && getRefreshToken()) {
    if (await refreshOnce()) {
      res = await send();
    }
  }
  return res;
}

// apiJson sends through apiFetch and throws a normalized TreboxApiError on a
// non-2xx response, otherwise returns the parsed JSON body.
export async function apiJson<T>(
  input: string,
  init?: RequestInit,
): Promise<T> {
  const res = await apiFetch(input, init);
  if (!res.ok) {
    throw await toApiError(res);
  }
  return res.json() as Promise<T>;
}

// --- session-management surface (tenant list + switch) ---

export interface TenantBrief {
  id: string;
  name: string;
  slug: string;
}

// listTenants returns the tenants the caller belongs to; empty on any failure
// so the shell renders a graceful single-tenant fallback rather than throwing.
export async function listTenants(): Promise<TenantBrief[]> {
  try {
    const body = await apiJson<{ tenants?: TenantBrief[] }>(
      '/api/auth/tenants',
    );
    return body.tenants ?? [];
  } catch {
    return [];
  }
}

// switchTenant re-issues the JWT pair for a tenant the caller already belongs
// to and persists the new session (tenant_id supplied; principal recovered
// from the JWT).
export async function switchTenant(tenantId: string): Promise<void> {
  const body = await apiJson<{ access_token?: string; refresh_token?: string }>(
    '/api/auth/switch-tenant',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tenant_id: tenantId }),
    },
  );
  if (!body.access_token) {
    throw new TreboxApiError(500, 'switch-tenant 响应缺少 access_token');
  }
  persistSession(body.access_token, body.refresh_token, tenantId);
}
